import { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Manages mutual-exclusion state for submenus at a given menu level.
 *
 * Only one submenu can be open at a time within a shared `SubmenuState`.
 * Both structured `MenuItem` hover-submenus and `SubmenuRow` instances
 * consume the same state via `SharedSubmenuStateContext`, so mutual
 * exclusion works across both paths automatically.
 */
export interface SubmenuState {
  /** The `key` of the currently active (open) submenu, or `null` if none. */
  activeSubmenuKey: string | null;
  /** Open the submenu identified by `key`, closing any other open submenu. */
  open: (key: string) => void;
  /** Close the submenu identified by `key` (no-op if it's not the active one). */
  close: (key: string) => void;
  /** Close whatever submenu is currently open. */
  closeAll: () => void;
  /** Check whether a specific submenu key is the active one. */
  isOpen: (key: string) => boolean;
}

/**
 * Creates a new, independent `SubmenuState` instance.
 *
 * Called once inside `NestedPopup` to create the shared
 * submenu state for each menu level, then provided to descendants via
 * `SharedSubmenuStateContext`.
 */
export const useSubmenuState = (): SubmenuState => {
  const [activeSubmenuKey, setActiveSubmenuKey] = useState<string | null>(null);

  const open = useCallback((key: string) => {
    setActiveSubmenuKey(key);
  }, []);

  const close = useCallback((key: string) => {
    setActiveSubmenuKey((current) => (current === key ? null : current));
  }, []);

  const closeAll = useCallback(() => {
    setActiveSubmenuKey(null);
  }, []);

  const isOpen = useCallback(
    (key: string) => activeSubmenuKey === key,
    [activeSubmenuKey]
  );

  return useMemo(
    () => ({ activeSubmenuKey, open, close, closeAll, isOpen }),
    [activeSubmenuKey, open, close, closeAll, isOpen]
  );
};

/**
 * React context for the shared submenu state at each menu level.
 *
 * `NestedPopup` creates a `SubmenuState` (via `useSubmenuState()`) and
 * provides it through this context. Both structured `MenuItem` submenus
 * and `SubmenuRow` instances read/write this same state, so mutual
 * exclusion works across both paths.
 */
export const SharedSubmenuStateContext = createContext<SubmenuState | null>(null);
SharedSubmenuStateContext.displayName = 'SharedSubmenuStateContext';

/**
 * Access the shared `SubmenuState` from context.
 *
 * Returns `null` if no `SharedSubmenuStateContext` provider is present.
 */
export const useSharedSubmenuState = (): SubmenuState | null => {
  return useContext(SharedSubmenuStateContext);
};
